import {
  ChangeDetectorRef,
  Component,
  inject,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';

import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { TagsAnalytics } from '@core/analytics-legacy';
import { AnalyticsMode } from '@core/analytics-types';
import { filterBoolean } from '@core/common-legacy';
import type { Attribute } from '@core/http-types';
import { SourcesActions } from '@core/tags-and-sources-data';
import type { Editable } from '@core/utils-legacy';

import { CandidateSourcesTypeaheadWrapperComponent } from '../../wrappers/candidate-sources-typeahead-wrapper/candidate-sources-typeahead-wrapper.component';
import { candidateIdSelector, editableSourcesSelector } from '../selectors';

@Component({
  selector: 'rt-candidate-sources-container',
  templateUrl: './candidate-sources-container.component.html',
  styleUrls: ['./candidate-sources-container.component.less'],
  providers: [TagsAnalytics],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CandidateSourcesTypeaheadWrapperComponent],
})
export class CandidateSourcesContainerComponent {
  private readonly store = inject(Store);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly analytics = inject(TagsAnalytics);

  public readonly withHeading = input<boolean>(true);
  public readonly withEmptyState = input<boolean>(false);
  public readonly hasNoMargin = input<boolean>(false);
  public sources: Editable<Array<{ value: string; processing: boolean }>>;

  private sources$: Observable<Editable<Array<{ value: any; processing: boolean }>>> =
    this.store.select(editableSourcesSelector);
  private ngOnDestroy$ = new Subject<void>();
  private candidateId$ = this.store.select(candidateIdSelector);

  public ngOnInit(): void {
    this.sources$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(sources => {
      this.sources = sources;
      this.cdRef.detectChanges();
    });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public saveSource(attr: Attribute): void {
    this.updateSources(attr.value);

    this.candidateId$.pipe(filterBoolean, take(1)).subscribe((candidateId: number) => {
      this.store.dispatch(
        SourcesActions.createSource({ payload: { candidateId, sourceName: attr.value } }),
      );
    });
    this.analytics.trackSourceAdded(AnalyticsMode.Single);
  }

  public deleteSource(attr: Attribute): void {
    this.candidateId$.pipe(filterBoolean, take(1)).subscribe((candidateId: number) => {
      this.store.dispatch(
        SourcesActions.deleteCandidateSources({ payload: { candidateId, sourceName: attr.value } }),
      );
    });
  }

  private updateSources(tagName: string): void {
    this.sources = {
      ...this.sources,
      value: [...(this.sources.value || []), { value: tagName, processing: true }],
    };
    this.cdRef.detectChanges();
  }
}
